// The five steps of the import wizard, in order. A step is reachable once the one before it has its
// input: the file must be read before the header row, the mapping and the review make sense. The
// functions are pure; the stepper component only draws them.

export const importSteps = [
  { key: "upload", label: "Upload" },
  { key: "header", label: "Sheet and header row" },
  { key: "map", label: "Map columns" },
  { key: "review", label: "Review" },
  { key: "save", label: "Save" },
] as const;

export type ImportStepKey = (typeof importSteps)[number]["key"];

export const importStepKeys: readonly ImportStepKey[] = importSteps.map((step) => step.key);

export type StepContext = Readonly<{
  /** The server has read a file (a preview), or a saved draft is open. */
  hasSource: boolean;
}>;

/** The step may be opened. The upload step is always open; the others need a source. */
export function isStepReachable(step: ImportStepKey, context: StepContext): boolean {
  return step === "upload" || context.hasSource;
}

export function stepNumber(step: ImportStepKey): number {
  return importStepKeys.indexOf(step) + 1;
}

export function nextStep(step: ImportStepKey, context: StepContext): ImportStepKey | null {
  const next = importStepKeys[importStepKeys.indexOf(step) + 1];
  return next !== undefined && isStepReachable(next, context) ? next : null;
}

export function previousStep(step: ImportStepKey): ImportStepKey | null {
  return importStepKeys[importStepKeys.indexOf(step) - 1] ?? null;
}

export function isImportStepKey(value: unknown): value is ImportStepKey {
  return typeof value === "string" && (importStepKeys as readonly string[]).includes(value);
}
